import React, { useState, useEffect } from 'react';
import {
  X, Copy, CircleCheck as CheckCircle2, FileText, Download, Eye,
  ChevronLeft, ChevronRight, TriangleAlert as AlertTriangle, ImagePlus,
  Loader as Loader2, Phone, Mail, MapPin, Hash, CreditCard, Zap, Users
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ClientData {
  id: string;
  client_name: string;
  cpf: string;
  phone: string;
  relative_phones: { numero: string; relacao: string }[];
  email: string;
  address: string;
  business_address: string;
  pix_key: string;
  code: string;
  contract_files: { id: string; file_name: string; file_url: string; file_type: string }[];
  contracts: {
    id: string;
    loan_amount: number;
    total_amount: number;
    created_at: string;
    payment_status: string;
    ruta: number;
    calculated_balance: number;
    installments_data: {
      id: string;
      due_date: string;
      amount: number;
      paid_amount: number | null;
      status: string;
    }[];
  }[];
}

interface Props {
  contractId: string;
  onClose: () => void;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatCPF = (cpf: string) => {
  const n = cpf.replace(/\D/g, '');
  return n.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

const formatPhone = (phone: string) => {
  const n = phone.replace(/\D/g, '');
  if (n.length === 11) return n.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (n.length === 10) return n.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return phone;
};

const calculateOutstandingBalance = (installments: { amount: number; paid_amount: number | null }[]) =>
  installments.reduce((total, inst) => total + inst.amount - (inst.paid_amount || 0), 0);

const isImage = (fileType: string) => fileType.startsWith('image/');
const isPDF = (fileType: string) => fileType === 'application/pdf';
const isVideo = (fileType: string) => fileType.startsWith('video/');

export default function ClientDetailModal({ contractId, onClose }: Props) {
  const { user } = useAuth();
  const [client, setClient] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);
  const [fileErrors, setFileErrors] = useState<{ [id: string]: boolean }>({});
  const [plaquinhaFileId, setPlaquinhaFileId] = useState<string | null>(null);
  const [generatingPlaquinha, setGeneratingPlaquinha] = useState(false);

  useEffect(() => {
    fetchClient();
  }, [contractId]);

  const fetchClient = async () => {
    setLoading(true);
    try {
      const { data: firstContract, error: firstError } = await supabase
        .from('contracts')
        .select('cpf')
        .eq('id', contractId)
        .maybeSingle();

      if (firstError || !firstContract) return;

      const { data: contracts, error } = await supabase
        .from('contracts')
        .select(`
          id,
          client_name,
          cpf,
          phone,
          relative_phones,
          email,
          address,
          business_address,
          pix_key,
          code,
          contract_files(*),
          loan_amount,
          total_amount,
          created_at,
          payment_status,
          ruta,
          installments_data:installments(id, due_date, amount, paid_amount, status)
        `)
        .eq('cpf', firstContract.cpf)
        .eq('unit_id', user?.unit_id)
        .order('created_at', { ascending: false });

      if (error || !contracts || contracts.length === 0) return;

      const base = contracts[0];
      const allFiles = new Map<string, any>();
      const contractsList = contracts.map(c => {
        (c.contract_files || []).forEach((f: any) => allFiles.set(f.id, f));
        return {
          id: c.id,
          loan_amount: c.loan_amount,
          total_amount: c.total_amount,
          created_at: c.created_at,
          payment_status: c.payment_status,
          ruta: c.ruta,
          calculated_balance: calculateOutstandingBalance(c.installments_data || []),
          installments_data: [...(c.installments_data || [])].sort((a, b) => a.due_date.localeCompare(b.due_date))
        };
      });

      setClient({
        id: base.id,
        client_name: base.client_name,
        cpf: base.cpf,
        phone: base.phone,
        relative_phones: base.relative_phones || [],
        email: base.email,
        address: base.address,
        business_address: base.business_address,
        pix_key: base.pix_key,
        code: base.code,
        contract_files: Array.from(allFiles.values()),
        contracts: contractsList
      });
      setCurrentFileIndex(0);
      setFileErrors({});
    } finally {
      setLoading(false);
    }
  };

  const handleFileError = (fileId: string) => {
    setFileErrors(prev => ({ ...prev, [fileId]: true }));
  };

  const handleFileDownload = (fileUrl: string, fileName: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getCurrentFile = () => client?.contract_files[currentFileIndex];

  const getSelectedPlaquinhaFile = () =>
    client?.contract_files.find(f => f.id === plaquinhaFileId);

  const handleTogglePlaquinha = () => {
    const file = getCurrentFile();
    if (!file || !isImage(file.file_type)) return;
    setPlaquinhaFileId(prev => prev === file.id ? null : file.id);
  };

  const generatePlaquinha = async () => {
    const file = getSelectedPlaquinhaFile();
    if (!file || !client) return;
    setGeneratingPlaquinha(true);
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error('Erro ao carregar imagem'));
        img.src = file.file_url;
      });

      const barHeight = 70;
      const padding = 20;
      const canvasWidth = Math.max(img.width, 400);
      const scale = canvasWidth / img.width;
      const imgHeight = img.height * scale;
      const canvasHeight = barHeight + padding + imgHeight + padding + barHeight;

      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
      const ctx = canvas.getContext('2d')!;

      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);

      ctx.drawImage(img, 0, barHeight + padding, canvasWidth, imgHeight);

      const headerFontSize = Math.max(24, canvasWidth * 0.05);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = `bold ${headerFontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('PROCURA-SE CALOTEIRO!!', canvasWidth / 2, barHeight / 2);

      const footerFontSize = Math.max(20, canvasWidth * 0.04);
      ctx.font = `bold ${footerFontSize}px Arial, sans-serif`;
      ctx.fillText(
        client.client_name.toUpperCase(),
        canvasWidth / 2,
        barHeight + padding + imgHeight + padding + barHeight / 2
      );

      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `plaquinha-${client.client_name.replace(/\s+/g, '-').toLowerCase()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Erro ao gerar plaquinha:', err);
      alert('Erro ao gerar a plaquinha. A imagem pode nao permitir acesso externo.');
    } finally {
      setGeneratingPlaquinha(false);
    }
  };

  const handleCopy = () => {
    if (!client) return;
    const text = `Dados do Cliente:
Nome: ${client.client_name}
CPF: ${client.cpf}
Telefone: ${client.phone}

Telefones dos Parentes:
${client.relative_phones.map(p => `${p.relacao}: ${p.numero}`).join('\n')}

Endereço Residencial: ${client.address}
Endereço Comercial: ${client.business_address}

${client.contracts.map(c => `Valor do Empréstimo: ${formatCurrency(c.loan_amount)}
Valor Total: ${formatCurrency(c.total_amount)}
Data: ${new Date(c.created_at).toLocaleDateString('pt-BR')}
Ruta: ${c.ruta}`).join('\n\n')}`.trim();

    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ─── helpers for installment status ───────────────────────────────────────
  const todayStr = new Date().toISOString().split('T')[0];

  const getInstallmentStatus = (inst: { status: string; due_date: string; paid_amount: number | null; amount: number }) => {
    if (inst.status === 'paid' || (inst.paid_amount && inst.paid_amount >= inst.amount)) return 'paid';
    if (inst.paid_amount && inst.paid_amount > 0 && inst.paid_amount < inst.amount) {
      if (inst.due_date < todayStr) return 'partial_overdue';
      return 'partial';
    }
    if (inst.due_date < todayStr) return 'overdue';
    return 'pending';
  };

  const getContractStatusBadge = (balance: number) => {
    if (balance < 0) return { label: 'Pago c/ juros', cls: 'bg-blue-100 text-blue-700' };
    if (balance === 0) return { label: 'Quitado', cls: 'bg-emerald-100 text-emerald-700' };
    return { label: 'Em aberto', cls: 'bg-red-100 text-red-600' };
  };

  // ─── initials avatar ──────────────────────────────────────────────────────
  const initials = client?.client_name
    ? client.client_name.trim().split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:rounded-2xl shadow-2xl flex flex-col max-h-[95vh] sm:max-h-[92vh] sm:max-w-4xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* ── STICKY HEADER ── */}
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 sm:px-6 py-4 flex items-center gap-3 flex-shrink-0">
          {/* Avatar */}
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-sm">
            <span className="text-white text-sm font-bold">{initials}</span>
          </div>

          {/* Title + name */}
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide leading-none mb-0.5">Detalhes do Cliente</p>
            {client && (
              <h2 className="text-base sm:text-lg font-bold text-gray-900 leading-tight truncate">
                {client.client_name}
                {client.code && (
                  <span className="ml-2 text-xs font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md align-middle">#{client.code}</span>
                )}
              </h2>
            )}
            {!client && !loading && (
              <h2 className="text-base font-bold text-gray-900">Cliente</h2>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {client && (
              <button
                onClick={handleCopy}
                aria-label="Copiar dados do cliente"
                className={`h-9 flex items-center gap-1.5 px-3 text-sm font-medium rounded-xl transition-colors ${
                  copied
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span className="hidden sm:inline">{copied ? 'Copiado!' : 'Copiar'}</span>
              </button>
            )}
            <button
              onClick={onClose}
              aria-label="Fechar modal"
              className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </div>

        {/* ── SCROLL CONTENT ── */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5">

          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-gray-400">Carregando dados...</p>
            </div>
          )}

          {/* Error */}
          {!loading && !client && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
              <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-gray-600 font-medium">Não foi possível carregar os dados do cliente.</p>
            </div>
          )}

          {!loading && client && (
            <>
              {/* ── 1. DADOS DO CLIENTE ── */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <CreditCard className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Informações do Cliente</h3>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Nome</p>
                    <p className="text-sm font-semibold text-gray-900">{client.client_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">CPF</p>
                    <p className="text-sm font-semibold text-gray-900 font-mono">{formatCPF(client.cpf)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Telefone</p>
                    <a
                      href={`https://wa.me/55${client.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors"
                    >
                      <Phone className="w-3.5 h-3.5" />
                      {formatPhone(client.phone)}
                    </a>
                  </div>
                  {client.code && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Código</p>
                      <div className="flex items-center gap-1">
                        <Hash className="w-3.5 h-3.5 text-gray-400" />
                        <p className="text-sm font-semibold text-gray-900">{client.code}</p>
                      </div>
                    </div>
                  )}
                  {client.email && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Email</p>
                      <div className="flex items-center gap-1 min-w-0">
                        <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-gray-900 break-all">{client.email}</p>
                      </div>
                    </div>
                  )}
                  {client.pix_key && (
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">Chave PIX</p>
                      <div className="flex items-center gap-1 min-w-0">
                        <Zap className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                        <p className="text-sm font-semibold text-gray-900 break-all">{client.pix_key}</p>
                      </div>
                    </div>
                  )}
                </div>
              </section>

              {/* ── 2. TELEFONES DOS PARENTES ── */}
              {client.relative_phones.filter(p => p.numero?.trim()).length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                    <Users className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contatos dos Parentes</h3>
                  </div>
                  <div className="p-4 space-y-2">
                    {client.relative_phones.filter(p => p.numero?.trim()).map((phone, index) => (
                      <div key={index} className="flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-gray-500">
                              {phone.relacao?.trim().charAt(0).toUpperCase() || '?'}
                            </span>
                          </div>
                          <span className="text-sm text-gray-600 truncate">{phone.relacao}</span>
                        </div>
                        <a
                          href={`https://wa.me/55${phone.numero.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 hover:text-emerald-700 transition-colors flex-shrink-0"
                        >
                          <Phone className="w-3.5 h-3.5" />
                          {formatPhone(phone.numero)}
                        </a>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ── 3. ENDEREÇOS ── */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <MapPin className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Endereços</h3>
                </div>
                <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Residencial</p>
                    <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                      {client.address || <span className="text-gray-400 italic">Não informado</span>}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-3">
                    <p className="text-xs text-gray-400 uppercase tracking-wide mb-1">Comercial</p>
                    <p className="text-sm font-medium text-gray-800 break-words leading-relaxed">
                      {client.business_address || <span className="text-gray-400 italic">Não informado</span>}
                    </p>
                  </div>
                </div>
              </section>

              {/* ── 4. DOCUMENTOS ── */}
              {client.contract_files.length > 0 && (
                <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Documentos</h3>
                    </div>
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                      {client.contract_files.length} arquivo{client.contract_files.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <div className="p-4 space-y-4">
                    {/* Navigation bar */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setCurrentFileIndex(i => Math.max(0, i - 1))}
                        disabled={currentFileIndex === 0}
                        aria-label="Arquivo anterior"
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          currentFileIndex === 0
                            ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                        }`}
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <p className="flex-1 text-sm font-medium text-gray-700 truncate text-center">
                        {getCurrentFile()?.file_name}
                      </p>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {currentFileIndex + 1}/{client.contract_files.length}
                      </span>
                      <button
                        onClick={() => setCurrentFileIndex(i => Math.min(client.contract_files.length - 1, i + 1))}
                        disabled={currentFileIndex === client.contract_files.length - 1}
                        aria-label="Próximo arquivo"
                        className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${
                          currentFileIndex === client.contract_files.length - 1
                            ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-600'
                        }`}
                      >
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </div>

                    {/* File viewer */}
                    {getCurrentFile() && (
                      <div className="rounded-xl bg-gray-50 border border-gray-100 overflow-hidden">
                        <div className="flex items-center justify-center min-h-[220px] sm:min-h-[320px] p-3">
                          {!fileErrors[getCurrentFile()!.id] ? (
                            <>
                              {isImage(getCurrentFile()!.file_type) ? (
                                <img
                                  src={getCurrentFile()!.file_url}
                                  alt={getCurrentFile()!.file_name}
                                  className="max-w-full max-h-[220px] sm:max-h-[400px] object-contain rounded-lg shadow-sm"
                                  onError={() => handleFileError(getCurrentFile()!.id)}
                                />
                              ) : isVideo(getCurrentFile()!.file_type) ? (
                                <video
                                  src={getCurrentFile()!.file_url}
                                  controls
                                  className="max-w-full max-h-[220px] sm:max-h-[400px] rounded-lg shadow-sm"
                                  onError={() => handleFileError(getCurrentFile()!.id)}
                                />
                              ) : isPDF(getCurrentFile()!.file_type) ? (
                                <div className="w-full h-[220px] sm:h-[400px] rounded-lg overflow-hidden">
                                  <iframe
                                    src={getCurrentFile()!.file_url}
                                    className="w-full h-full"
                                    title={getCurrentFile()!.file_name}
                                    onError={() => handleFileError(getCurrentFile()!.id)}
                                  />
                                </div>
                              ) : (
                                <div className="text-center py-8">
                                  <div className="w-16 h-16 rounded-2xl bg-gray-200 flex items-center justify-center mx-auto mb-3">
                                    <FileText className="w-8 h-8 text-gray-400" />
                                  </div>
                                  <p className="text-sm font-medium text-gray-600 break-words">{getCurrentFile()!.file_name}</p>
                                  <p className="text-xs text-gray-400 mt-1">{getCurrentFile()!.file_type}</p>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="text-center py-8">
                              <div className="w-12 h-12 rounded-2xl bg-red-100 flex items-center justify-center mx-auto mb-3">
                                <AlertTriangle className="w-6 h-6 text-red-500" />
                              </div>
                              <p className="text-sm font-medium text-red-600">Erro ao carregar o arquivo</p>
                              <p className="text-xs text-gray-400 mt-1 break-words">{getCurrentFile()!.file_name}</p>
                            </div>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex flex-wrap gap-2 px-3 pb-3">
                          <button
                            onClick={() => handleFileDownload(getCurrentFile()!.file_url, getCurrentFile()!.file_name)}
                            aria-label="Download do arquivo"
                            className="flex-1 sm:flex-none h-9 flex items-center justify-center gap-1.5 px-3 text-sm font-medium bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                          <a
                            href={getCurrentFile()!.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Abrir em nova aba"
                            className="flex-1 sm:flex-none h-9 flex items-center justify-center gap-1.5 px-3 text-sm font-medium bg-gray-700 text-white rounded-xl hover:bg-gray-800 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                            Abrir
                          </a>
                          {isImage(getCurrentFile()!.file_type) && (
                            <button
                              onClick={handleTogglePlaquinha}
                              aria-label="Selecionar para plaquinha"
                              className={`flex-1 sm:flex-none h-9 flex items-center justify-center gap-1.5 px-3 text-sm font-medium rounded-xl transition-colors ${
                                plaquinhaFileId === getCurrentFile()!.id
                                  ? 'bg-amber-500 text-white hover:bg-amber-600'
                                  : 'bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200'
                              }`}
                            >
                              <ImagePlus className="w-4 h-4" />
                              {plaquinhaFileId === getCurrentFile()!.id ? 'Selecionada' : 'Plaquinha'}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Thumbnails strip */}
                    <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                      {client.contract_files.map((file, index) => (
                        <button
                          key={file.id}
                          onClick={() => setCurrentFileIndex(index)}
                          className={`relative flex-shrink-0 w-[72px] rounded-xl border-2 overflow-hidden transition-all ${
                            currentFileIndex === index
                              ? 'border-blue-500 shadow-sm shadow-blue-200'
                              : 'border-gray-100 hover:border-gray-300'
                          }`}
                        >
                          {plaquinhaFileId === file.id && (
                            <div className="absolute top-1 right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center z-10">
                              <ImagePlus className="w-2.5 h-2.5 text-white" />
                            </div>
                          )}
                          <div className="h-12 bg-gray-100 flex items-center justify-center">
                            {isImage(file.file_type) ? (
                              <img
                                src={file.file_url}
                                alt={file.file_name}
                                className="w-full h-full object-cover"
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            ) : (
                              <FileText className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <div className="px-1 py-1 bg-white">
                            <p className="text-xs text-gray-500 truncate text-center leading-tight">{file.file_name}</p>
                          </div>
                          {fileErrors[file.id] && (
                            <div className="absolute inset-0 bg-red-50/80 flex items-center justify-center">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>

                    {/* Plaquinha section */}
                    {plaquinhaFileId && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-200 flex items-center justify-center flex-shrink-0">
                            <ImagePlus className="w-4 h-4 text-amber-700" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-amber-800">
                              {getSelectedPlaquinhaFile()?.file_name}
                            </p>
                            <p className="text-xs text-amber-600 mt-0.5">
                              A plaquinha sera gerada com o nome do cliente no rodape
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={generatePlaquinha}
                          disabled={generatingPlaquinha}
                          className="w-full h-11 flex items-center justify-center gap-2 bg-gray-900 text-white rounded-xl hover:bg-black transition-colors text-sm font-bold disabled:opacity-50"
                        >
                          {generatingPlaquinha
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <ImagePlus className="w-4 h-4" />}
                          {generatingPlaquinha ? 'Gerando...' : 'FAZER PLAQUINHA'}
                        </button>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ── 5. HISTÓRICO DE CONTRATOS ── */}
              <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50 bg-gray-50/60">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Histórico de Contratos</h3>
                  </div>
                  <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                    {client.contracts.length} contrato{client.contracts.length !== 1 ? 's' : ''}
                  </span>
                </div>

                <div className="p-4 space-y-4">
                  {client.contracts.map((contract, contractIdx) => {
                    const badge = getContractStatusBadge(contract.calculated_balance);
                    return (
                      <div
                        key={contract.id}
                        className={`rounded-xl border overflow-hidden ${
                          contract.calculated_balance > 0 ? 'border-red-100' : 'border-emerald-100'
                        }`}
                      >
                        {/* Contract header */}
                        <div className={`px-4 py-3 flex flex-wrap items-start justify-between gap-3 ${
                          contract.calculated_balance > 0 ? 'bg-red-50/50' : 'bg-emerald-50/50'
                        }`}>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-base font-bold text-gray-900">{formatCurrency(contract.loan_amount)}</p>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>
                                {badge.label}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {new Date(contract.created_at).toLocaleDateString('pt-BR')} &bull; Rota {contract.ruta}
                            </p>
                          </div>
                          <div className="text-right space-y-0.5">
                            <div className="flex items-center justify-end gap-2 text-xs">
                              <span className="text-gray-400">Total:</span>
                              <span className="font-semibold text-gray-700">{formatCurrency(contract.total_amount)}</span>
                            </div>
                            {(() => {
                              const totalPaid = contract.total_amount - contract.calculated_balance;
                              return totalPaid > 0 && contract.calculated_balance > 0 ? (
                                <div className="flex items-center justify-end gap-2 text-xs">
                                  <span className="text-emerald-500">Pago:</span>
                                  <span className="font-semibold text-emerald-600">{formatCurrency(totalPaid)}</span>
                                </div>
                              ) : null;
                            })()}
                            <div className="flex items-center justify-end gap-2 text-sm mt-1">
                              <span className="text-gray-400 text-xs">Saldo devedor:</span>
                              <span className={`font-bold ${contract.calculated_balance <= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {contract.calculated_balance < 0
                                  ? `+${formatCurrency(Math.abs(contract.calculated_balance))} juros`
                                  : contract.calculated_balance === 0
                                  ? 'Quitado'
                                  : formatCurrency(contract.calculated_balance)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Installments grid */}
                        <div className="p-3">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2.5">
                            Parcelas ({contract.installments_data.length})
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {contract.installments_data.map((inst, index) => {
                              const status = getInstallmentStatus(inst);
                              const statusConfig = {
                                paid: { label: 'Paga', cls: 'bg-emerald-100 text-emerald-700' },
                                overdue: { label: 'Atrasada', cls: 'bg-red-100 text-red-600' },
                                pending: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
                                partial: { label: 'Parcial', cls: 'bg-blue-100 text-blue-700' },
                                partial_overdue: { label: 'Parcial', cls: 'bg-orange-100 text-orange-700' },
                              }[status];
                              const paidAmt = inst.paid_amount || 0;
                              const remaining = inst.amount - paidAmt;
                              const isPartial = status === 'partial' || status === 'partial_overdue';

                              return (
                                <div
                                  key={inst.id}
                                  className={`rounded-lg p-2.5 border text-xs ${
                                    status === 'paid'
                                      ? 'bg-white border-gray-100'
                                      : status === 'overdue'
                                      ? 'bg-red-50 border-red-100'
                                      : isPartial
                                      ? 'bg-blue-50/50 border-blue-100'
                                      : 'bg-white border-gray-100'
                                  }`}
                                >
                                  <div className="flex items-center justify-between gap-1 mb-1.5">
                                    <span className="font-semibold text-gray-600">#{index + 1}</span>
                                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold leading-none ${statusConfig.cls}`}>
                                      {statusConfig.label}
                                    </span>
                                  </div>
                                  {isPartial ? (
                                    <>
                                      <p className="font-bold text-gray-900">Resta {formatCurrency(remaining)}</p>
                                      <p className="text-emerald-600 font-medium mt-0.5">Pago: {formatCurrency(paidAmt)}</p>
                                      <p className="text-gray-400 mt-0.5">de {formatCurrency(inst.amount)}</p>
                                    </>
                                  ) : status === 'paid' ? (
                                    <>
                                      <p className="font-bold text-gray-900">{formatCurrency(inst.amount)}</p>
                                      {paidAmt > 0 && paidAmt !== inst.amount && (
                                        <p className="text-emerald-600 font-medium mt-0.5">Pago: {formatCurrency(paidAmt)}</p>
                                      )}
                                    </>
                                  ) : (
                                    <p className="font-bold text-gray-900">{formatCurrency(inst.amount)}</p>
                                  )}
                                  <p className="text-gray-400 mt-0.5">
                                    {new Date(inst.due_date + 'T00:00:00Z').toLocaleDateString('pt-BR')}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
